/**
 * IPermitr.ts - IPermitr - Standard interface for permission checks
 * 
 * CHANGE HISTORY:
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251020_0732 [95fb3a1] danielbeer: fix: complete test infrastructure - all tests passing
 * - 251020_0617 [9601b37] danielbeer: feat: implement interface-based architecture with enforcement
 */
export interface IPermitr {
  /**
   * Check if user can perform an action
   * @param action - Action to check (e.g., 'task.create', 'task.delete')
   * @param context - Optional context for the check
   * @returns boolean True if user has permission
   */
  canPerform(action: string, context?: Record<string, any>): boolean;

  /**
   * Check if user has a specific role
   * @param role - Role to check
   * @returns boolean True if user has role
   */
  hasRole(role: string): boolean;

  /**
   * Check if user has a specific permission
   * @param permission - Permission to check
   * @returns boolean True if user has permission
   */
  hasPermission(permission: string): boolean;

  /**
   * Get all permissions for current user
   * @returns string[] Array of permission strings
   */
  getPermissions(): string[];

  /**
   * Get all roles for current user
   * @returns string[] Array of role strings
   */
  getRoles(): string[];
}

/**
 * Type guard to check if an object implements IPermitr
 */
export function isPermitr(obj): obj is IPermitr {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    typeof obj.canPerform === 'function' &&
    typeof obj.hasRole === 'function' &&
    typeof obj.hasPermission === 'function' &&
    typeof obj.getPermissions === 'function' &&
    typeof obj.getRoles === 'function'
  );
}
